// 图片放大：点缩略图全屏看原图；滚轮 / 触控板捏合缩放（以鼠标位置为中心），按住拖动看任意部分，双击在「适合宽度」和 100% 之间切换；Esc 或 ✕ 关闭
import { useCallback, useEffect, useRef, useState, type PointerEvent as RPointerEvent, type WheelEvent as RWheelEvent } from "react";
import { createPortal } from "react-dom";

export function useLightbox() {
  const [src, setSrc] = useState<string | null>(null);
  const node = src ? <Lightbox src={src} onClose={() => setSrc(null)} /> : null;
  return { open: setSrc, node };
}

interface View { scale: number; x: number; y: number }
const MIN = 0.1;
const MAX = 8;
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

export function Lightbox({ src, onClose }: { src: string; onClose: () => void }) {
  const box = useRef<HTMLDivElement>(null);
  const [nat, setNat] = useState<{ w: number; h: number } | null>(null);
  const [view, setView] = useState<View>({ scale: 1, x: 0, y: 0 });
  const drag = useRef<{ px: number; py: number; x: number; y: number; moved: boolean } | null>(null);

  // 适合宽度（长截图从顶部开始看）；图比窗口矮就整张居中
  const fit = useCallback((n = nat) => {
    const el = box.current;
    if (!el || !n) return;
    const s = Math.min(el.clientWidth / n.w, 1.5);
    const h = n.h * s;
    setView({ scale: s, x: (el.clientWidth - n.w * s) / 2, y: h < el.clientHeight ? (el.clientHeight - h) / 2 : 0 });
  }, [nat]);

  /** 以 (cx, cy)（相对容器）为中心缩放到 next */
  const zoomAt = useCallback((next: number, cx?: number, cy?: number) => {
    const el = box.current;
    if (!el) return;
    setView((v) => {
      const s = clamp(next, MIN, MAX);
      const ox = cx ?? el.clientWidth / 2;
      const oy = cy ?? el.clientHeight / 2;
      return { scale: s, x: ox - ((ox - v.x) * s) / v.scale, y: oy - ((oy - v.y) * s) / v.scale };
    });
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "+" || e.key === "=") zoomAt(view.scale * 1.25);
      else if (e.key === "-") zoomAt(view.scale / 1.25);
      else if (e.key === "0") fit();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, zoomAt, fit, view.scale]);

  // 滚轮：触控板捏合 / Ctrl+滚轮 = 缩放；普通滚轮 = 上下（Shift 左右）移动
  const onWheel = (e: RWheelEvent) => {
    const r = box.current!.getBoundingClientRect();
    if (e.ctrlKey || e.metaKey) zoomAt(view.scale * Math.exp(-e.deltaY * 0.01), e.clientX - r.left, e.clientY - r.top);
    else setView((v) => ({ ...v, x: v.x - (e.shiftKey ? e.deltaY : e.deltaX), y: v.y - (e.shiftKey ? 0 : e.deltaY) }));
  };
  // 原生 wheel 监听设为非 passive，才能挡住浏览器自己的页面缩放
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const stop = (e: WheelEvent) => e.preventDefault();
    el.addEventListener("wheel", stop, { passive: false });
    return () => el.removeEventListener("wheel", stop);
  }, []);

  const onDown = (e: RPointerEvent) => {
    if (e.button !== 0) return;
    (e.target as Element).setPointerCapture?.(e.pointerId);
    drag.current = { px: e.clientX, py: e.clientY, x: view.x, y: view.y, moved: false };
  };
  const onMove = (e: RPointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.px;
    const dy = e.clientY - d.py;
    if (Math.abs(dx) + Math.abs(dy) > 3) d.moved = true;
    setView((v) => ({ ...v, x: d.x + dx, y: d.y + dy }));
  };
  const onUp = () => { drag.current = null; };
  const onDouble = (e: React.MouseEvent) => {
    const r = box.current!.getBoundingClientRect();
    if (nat && Math.abs(view.scale - 1) > 0.05) zoomAt(1, e.clientX - r.left, e.clientY - r.top);
    else fit();
  };

  return createPortal(
    <div className="lightbox" role="dialog" aria-label="大图">
      <div className="lightbox-bar" onPointerDown={(e) => e.stopPropagation()}>
        <button className="btn" onClick={() => zoomAt(view.scale / 1.25)} aria-label="缩小">－</button>
        <span className="lightbox-pct">{Math.round(view.scale * 100)}%</span>
        <button className="btn" onClick={() => zoomAt(view.scale * 1.25)} aria-label="放大">＋</button>
        <button className="btn" onClick={() => fit()}>适合宽度</button>
        <button className="btn" onClick={() => zoomAt(1)}>100%</button>
        <span className="small lightbox-tip">拖动查看 · 捏合或 ⌘/Ctrl+滚轮缩放 · 双击切换</span>
        <button className="btn" onClick={onClose} aria-label="关闭">✕ 关闭</button>
      </div>
      <div ref={box} className="lightbox-stage" onWheel={onWheel} onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp} onDoubleClick={onDouble}>
        <img src={src} alt="截图大图" draggable={false}
          style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})` }}
          onLoad={(e) => { const n = { w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight }; setNat(n); fit(n); }} />
      </div>
    </div>,
    document.body,
  );
}
